import { Dispatch, SetStateAction, ReactNode } from 'react';

// ==================== Типы фильтров ====================

/** Типы элементов фильтра (picker) */
export type FilterType = 
    | 'list'
    | 'lazylist'
    | 'text'
    | 'number'
    | 'numberrange'
    | 'datetimerange'
    | 'timerange';

/** Типы отчётов для определения набора фильтров */
export type ReportFilterType =
    | 'universal'      // Главная (регистрации)
    | 'order'          // Заказы
    | 'payment'        // Платежи
    | 'webinar'        // Вебинары
    | 'ad'             // Реклама
    | 'visit'          // Визиты
    | 'session'        // Сессии
    | 'hit'            // Хиты
    | 'webinar-quality'// Качество вебинаров
    | 'webinar-comments' // Комментарии вебинаров
    | 'users'          // Пользователи
    | 'regs'           // Регистрации
    | 'orders'         // Список заказов
    | 'payments'       // Список платежей
    | 'visitors'       // Посетители
    | 'dashboard'      // Дашборд
    | 'crm-users'
    | 'crm-schools'
    | 'crm-orders'
    | 'crm-payments'
    | 'crm-requests'
    | 'crm-activity'
    | 'crm-details';

/** Режим работы picker-компонента */
export type PickerMode = 'normal' | 'compare' | 'modal';

// ==================== Интерфейсы данных фильтров (filters.ts) ====================

/** Базовый интерфейс элемента фильтра */
export interface IFilterItem {
    /** ID фильтра (используется на бэкенде) */
    id: string;
    /** Отображаемое название */
    name: string;
    /** Полное название для отображения в выбранных фильтрах */
    full_name?: string;
    /** Тип picker-а для выбора значения */
    type?: FilterType;
    /** Описание для tooltip */
    description?: string;
    /** Цвет (для корневых элементов) */
    color?: string;
    /** Иконка (для корневых элементов) */
    icon?: string;
    /** Флаг корневого элемента */
    root?: boolean;
    /** Дочерние элементы */
    children?: IFilterItem[];
    /** Показывать описание даже для элементов с children */
    visibleDescription?: boolean;
    /** Показывать кнопку "+" для добавления элементов списка */
    showListPlus?: boolean;
    /** Использовать поиск в lazylist */
    useSearch?: boolean;
    /** Предустановленные items для list (например, ошибки CRM) */
    items?: Array<{ id: number | string; name: string }>;
}

/** Интерфейс выбранного фильтра (добавляются поля при выборе) */
export interface ISelectedFilter extends IFilterItem {
    /** Индекс в массиве filters */
    arrayIndex?: number;
    /** Путь имён категорий до текущего фильтра */
    namePath?: string[];
    /** Путь id категорий до текущего фильтра */
    path?: string[];
    /** Ключ в дереве */
    key?: string;
    /** Выбранные значения (для list/lazylist) */
    checkeds?: Array<{ id: number | string; name: string }>;
    /** Элементы списка */
    items?: Array<{ id: number | string; name: string }>;
    /** Значение (для text/number) */
    value?: string | number;
    /** Начало диапазона */
    start?: string;
    /** Конец диапазона */
    end?: string;
    /** Тип календаря */
    calendar?: { type: string };
    /** Сохранённый поисковый запрос для автоматического применения при инициализации отчёта */
    savedSearch?: string;
}

/** Интерфейс корневой категории фильтров */
export interface IFilterCategory extends IFilterItem {
    root: true;
    color: string;
    icon: string;
    children: IFilterItem[];
}

// ==================== Интерфейсы для дерева фильтров ====================

/** Интерфейс элемента дерева фильтров (после prepareData) */
export interface FilterTreeNode extends IFilterItem {
    /** Ключ узла в дереве */
    key?: string;
    /** React-компонент для отображения названия */
    title?: () => ReactNode;
    /** React-компонент для отображения названия с подсветкой поиска */
    searchName?: ReactNode;
    /** Флаг листового узла (без children) */
    isLeaf?: boolean;
    /** Отключён ли узел */
    disabled?: boolean;
    /** Можно ли выбрать узел */
    selectable?: boolean;
    /** Можно ли отметить чекбоксом */
    checkable?: boolean;
    /** Отключён ли чекбокс */
    disableCheckbox?: boolean;
    /** Полное имя */
    full_name?: string;
    /** Путь имён */
    namePath?: string[];
    /** Путь id */
    path?: string[];
    /** Callback при выборе */
    onOk?: (value: boolean, withoutFilter?: boolean) => void;
    /** Дочерние узлы */
    children?: FilterTreeNode[];
}

// ==================== Props интерфейсы ====================

/** Props для ReportFilter компонента */
export interface ReportFilterProps {
    /** Тип отчёта для определения набора фильтров */
    reportType?: ReportFilterType;
    /** Кастомный список категорий фильтров */
    filtersFilter?: string[];
    /** Режим сравнения */
    isCompare?: boolean;
    /** Показывать сегменты */
    withSegment?: boolean;
    /** Показывать лейбл "Условия фильтра" */
    withLabel?: boolean;
    /** Показывать кнопку обновления */
    withRefresh?: boolean;
    /** Отключить фильтры */
    disabled?: boolean;
    /** Отключить фильтры атрибуции (Реклама, Регистрации) */
    disableAttr?: boolean;
    /** Не использовать параметры из URL */
    notParams?: boolean;
    /** Режим дашборда */
    isDashboard?: boolean;
    /** Фиксированный режим для дашборда */
    isFixed?: boolean;
    /** Элемент перед фильтрами */
    preElement?: ReactNode;
    /** Callback обновления дашборда */
    onUpdateDashboard?: () => void;
    /** Состояние кнопки запроса */
    reqBtnDisabled?: boolean;
    /** Setter для состояния кнопки запроса */
    setReqBtnDisabled?: Dispatch<SetStateAction<boolean>>;
    /** Видимость кнопки загрузки данных дашборда */
    fetchDashboardDataButtonVisible?: boolean;
    /** Setter видимости кнопки загрузки */
    setFetchDashboardDataButtonVisible?: Dispatch<SetStateAction<boolean>>;
}

/** Параметры для хука useFilterLogic */
export interface UseFilterLogicOptions {
    /** Тип отчёта */
    reportType?: ReportFilterType;
    /** Кастомный список категорий */
    filtersFilter?: string[];
    /** Отключить фильтры атрибуции */
    disableAttr?: boolean;
    /** Режим дашборда */
    isDashboard?: boolean;
    /** Фиксированный режим для дашборда */
    isFixed?: boolean;
    /** Callback активации кнопки запроса */
    onActivateRequest?: (disabled: boolean, withoutFilter?: boolean) => void;
}

/** Возвращаемое значение хука useFilterLogic */
export interface UseFilterLogicReturn {
    /** Данные дерева фильтров */
    treeData: FilterTreeNode[];
    /** Раскрытые ключи */
    expandedKeys: string[];
    /** Значение поиска */
    searchValue: string;
    /** Автораскрытие родителей */
    autoExpandParent: boolean;
    /** Видимость поповера */
    filterVisible: boolean;
    /** Сырые данные фильтров */
    rawData: IFilterItem[];
    /** Установить сырые данные */
    setRawData: Dispatch<SetStateAction<IFilterItem[]>>;
    /** Установить раскрытые ключи */
    setExpandedKeys: Dispatch<SetStateAction<string[]>>;
    /** Установить видимость поповера */
    setFilterVisible: Dispatch<SetStateAction<boolean>>;
    /** Обработчик раскрытия */
    onExpand: (keys: string[]) => void;
    /** Обработчик поиска */
    onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
    /** Очистить фильтры */
    onClearFilters: () => void;
    /** Загрузка данных для lazy-узлов */
    onLoadData: (node: FilterTreeNode) => Promise<void>;
}

/** Props для базового picker-компонента */
export interface BasePickerProps {
    /** ID фильтра */
    id: string;
    /** Индекс в массиве filters (для редактирования) */
    index?: number;
    /** Режим работы: normal, compare, modal */
    mode?: PickerMode;
    /** Префикс для compare режима */
    prefix?: string;
    /** Отключён ли picker */
    disabled?: boolean;
    /** Callback закрытия модального окна */
    modalVisibleHandler: (visible: boolean) => void;
    /** Данные выбранного фильтра (при редактировании) */
    itemData?: ISelectedFilter;
    /** Флаг режима списка */
    list?: boolean;
    /** Название фильтра */
    name?: string;
    /** Полное название */
    full_name?: string;
    /** Тип фильтра */
    type?: FilterType;
    /** Путь */
    path?: string[];
    /** Путь имён */
    namePath?: string[];
    /** Цвет */
    color?: string;
    /** Иконка */
    icon?: string;
    /** Показывать кнопку добавления */
    showListPlus?: boolean;
    /** Предустановленные items */
    items?: Array<{ id: number | string; name: string }>;
    /** Флаг для YandexMetrics */
    isYM?: boolean;
    /** Флаг модального режима */
    isModal?: boolean;
}

// ==================== Конфиг фильтров ====================

/** Конфигурация категорий фильтров для типа отчёта */
export interface FilterCategoryConfig {
    /** Категории фильтров для типа отчёта */
    categories: string[];
    /** Дополнительные фильтры для AD отчёта */
    adFilters?: boolean;
    /** Исключить период из вебинаров */
    excludeWebinarPeriod?: boolean;
}

/** Конфигурация фильтров по типу отчёта */
export const REPORT_FILTER_CONFIG: Record<ReportFilterType, FilterCategoryConfig> = {
    universal: {
        categories: [
            'Пользователи',
            'Регистрации',
            'Заявки',
            'Заказы',
            'Платежи',
            'Вебинары',
            'Посещение страницы',
        ],
    },
    order: {
        categories: ['Пользователи', 'Регистрации', 'Заказы', 'Платежи'],
    },
    payment: {
        categories: ['Пользователи', 'Заказы', 'Платежи'],
    },
    webinar: {
        categories: [
            'Пользователи',
            'Регистрации',
            'Заказы',
            'Платежи',
            'Вебинары',
        ],
    },
    ad: {
        categories: [
            'Пользователи',
            'Регистрации',
            'Заявки',
            'Заказы',
            'Платежи',
            'Вебинары',
            'Реклама',
        ],
        adFilters: true,
    },
    visit: {
        categories: [
            'Пользователи',
            'Регистрации',
            'Заявки',
            'Заказы',
            'Платежи',
            'Вебинары',
        ],
    },
    session: {
        categories: ['Сессии'],
    },
    hit: {
        categories: ['Сессии', 'Хиты'],
    },
    'webinar-quality': {
        categories: [
            'Пользователи',
            'Регистрации',
            'Заявки',
            'Заказы',
            'Платежи',
            'Вебинары',
        ],
        excludeWebinarPeriod: true,
    },
    'webinar-comments': {
        categories: ['Пользователи', 'Вебинары'],
    },
    users: {
        categories: ['Пользователи'],
    },
    regs: {
        categories: ['Пользователи', 'Регистрации'],
    },
    orders: {
        categories: ['Пользователи', 'Заказы'],
    },
    payments: {
        categories: ['Пользователи', 'Заказы', 'Платежи'],
    },
    visitors: {
        categories: ['Сессии'],
    },
    dashboard: {
        categories: [
            'Пользователи',
            'Регистрации',
            'Заявки',
            'Заказы',
            'Платежи',
            'Вебинары',
            'Сессии',
            'Хиты',
        ],
    },
    'crm-users': {
        categories: ['Пользователи (CRM)'],
    },
    'crm-schools': {
        categories: ['Школы (CRM)'],
    },
    'crm-orders': {
        categories: ['Заказы (CRM)'],
    },
    'crm-payments': {
        categories: ['Платежи (CRM)'],
    },
    'crm-requests': {
        categories: ['Запросы (CRM)'],
    },
    'crm-activity': {
        categories: ['Активность (CRM)'],
    },
    'crm-details': {
        categories: ['Детализация (CRM)'],
    },
};

// ==================== Конфиг pathname ====================

/** Конфигурация pathname -> reportType */
export const PATHNAME_TO_REPORT_TYPE: Record<string, ReportFilterType> = {
    '/': 'universal',
    '/users/ad-reports': 'ad',
    '/users/order-reports': 'order',
    '/users/payment-reports': 'payment',
    '/users/webinar-reports': 'webinar',
    '/users/visit-reports': 'visit',
    '/webinar/quality': 'webinar-quality',
    '/webinar/comments': 'webinar-comments',
    '/session/session': 'session',
    '/session/hits': 'hit',
    '/users': 'users',
    '/users/registrations': 'regs',
    '/users/orders': 'orders',
    '/users/payments': 'payments',
    '/users/visitors': 'visitors',
    '/dashboards': 'dashboard',
    '/crm/users': 'crm-users',
    '/crm/schools': 'crm-schools',
    '/crm/orders': 'crm-orders',
    '/crm/payments': 'crm-payments',
    '/crm/requests': 'crm-requests',
    '/crm/activity': 'crm-activity',
    '/crm/details': 'crm-details',
};

/** Pathnames, требующие особой обработки фильтров */
export const SPECIAL_FILTER_PATHNAMES = {
    AD_REPORTS: '/users/ad-reports',
    WEBINAR_QUALITY: '/webinar/quality',
    SOURCE_ADS: '/source/ads',
} as const;

// ==================== Lazy загрузка ====================

/** Названия категорий с lazy-загрузкой */
export const LAZY_FILTER_NAMES = new Set<string>([
    'Доп. поля регистрации',
    'Доп. поля пользователя',
    'Доп. поля заказа',
    'Доп. поля заявки',
]);

// ==================== Константы UI ====================

/** Категории, которые нужно скрыть при disableAttr */
export const DISABLED_ATTR_CATEGORIES = ['Реклама', 'Регистрации'];

/** Типы календарных периодов */
export const CALENDAR_TYPES = [
    'today',
    'yesterday',
    'thisweek',
    'lastweek',
    'thismonth',
    'lastmonth',
    'thisquart',
    'lastquart',
    'thisyear',
    'lastyear',
    'last7days',
    'last14days',
    'last30days',
    'last365days',
] as const;

export type CalendarType = typeof CALENDAR_TYPES[number];

// ==================== Параметры prepareData ====================

/** Параметры для функции prepareData */
export interface PrepareDataParams {
    /** Данные фильтров */
    data: IFilterItem[];
    /** Значение поиска */
    searchValue: string;
    /** Выбранные фильтры */
    filters: ISelectedFilter[];
    /** Отключить фильтры атрибуции */
    disableAttr: boolean;
    /** Родительские фильтры */
    filterParent?: ISelectedFilter[];
    /** Callback при выборе */
    onOk?: (value: boolean, withoutFilter?: boolean) => void;
    /** Флаг модального режима */
    isModal?: boolean;
    /** Флаг YandexMetrics */
    isYM?: boolean;
}
